import React from 'react'
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow'
import { fromUnixTime } from 'date-fns/fromUnixTime'

import { useTheme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'

import IconButton from '@mui/material/IconButton'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { Icon, Text } from '@mxenabled/mxui'
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro'

import { useGlobalCopyStore } from '../../../common'
import { Device } from '../../../core'
import { HeaderCell } from '../../../trends/components/trendstable/HeaderCell'
import { buildDynamicCopy } from '../../../core/utils/Localization'

interface DeviceTableProps {
  devices: Device[]
  onClickDeviceEdit: (device: Device) => void
  onClickDeviceDelete: (device: Device) => void
}

export const DeviceTable: React.FC<DeviceTableProps> = ({
  devices,
  onClickDeviceEdit,
  onClickDeviceDelete,
}) => {
  const theme = useTheme()
  const { settings: settingsCopy } = useGlobalCopyStore()

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: settingsCopy.mobile_devices_column_name,
      renderHeader: HeaderCell,
      renderCell: (params) => (
        <Stack alignItems="center" direction="row" gap={4}>
          <Text variant="body2">{params.value}</Text>
          <IconButton
            aria-label={settingsCopy.mobile_devices_edit_label}
            color="secondary"
            onClick={() => onClickDeviceEdit(params.row as Device)}
          >
            <Icon name="edit" />
          </IconButton>
        </Stack>
      ),
      sortable: true,
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'device_type',
      headerName: settingsCopy.mobile_devices_column_type,
      renderHeader: HeaderCell,
      sortable: true,
      hideSortIcons: false,
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'created_at',
      headerName: settingsCopy.mobile_devices_column_added,
      renderHeader: HeaderCell,
      valueFormatter: (value: number) =>
        formatDistanceToNow(fromUnixTime(value), { addSuffix: true }),
      sortable: true,
      hideSortIcons: false,
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'delete',
      headerName: '',
      renderHeader: HeaderCell,
      renderCell: (params) => (
        <Stack mr={4}>
          <IconButton
            aria-label={settingsCopy.mobile_devices_delete_label}
            color="secondary"
            onClick={() => onClickDeviceDelete(params.row as Device)}
          >
            <Icon name="delete" />
          </IconButton>
        </Stack>
      ),
      sortable: false,
      hideSortIcons: false,
      width: 58,
    },
  ]

  const rows = React.useMemo(() => {
    return devices.map((device) => ({
      id: device.guid,
      ...device,
      device_type: `${device.make} ${device.model}`,
    }))
  }, [devices])

  return (
    <Box sx={{ width: '100%' }}>
      <DataGridPro
        columns={columns}
        disableColumnFilter={true}
        disableColumnMenu={true}
        disableRowSelectionOnClick={true}
        rows={rows}
        slotProps={{
          footer: {
            sx: { justifyContent: 'flex-end' },
          },
        }}
        slots={{
          footer: () => (
            <Box
              sx={{
                backgroundColor: theme.palette.background.default,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                p: '12px 16px',
              }}
            >
              <Text color={theme.palette.text.secondary} variant="body2">
                {
                  buildDynamicCopy(
                    settingsCopy.mobile_devices_count,
                    <b>{devices.length}</b>,
                    <b>{devices.length}</b>,
                  ) as string
                }
              </Text>
            </Box>
          ),
        }}
        sx={{
          border: '1px solid',
          borderColor: theme.palette.neutral.light,
          borderRadius: '8px',
          '& .MuiDataGrid-columnHeader .MuiIconButton-root': {
            color: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            },
          },
          '& .MuiDataGrid-sortIcon': {
            color: theme.palette.primary.main,
          },
        }}
      />
    </Box>
  )
}

export default DeviceTable
